import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import comptabiliteService, { Balance as BalanceData } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

const Balance: React.FC = () => {
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState<BalanceData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await comptabiliteService.balance(startDate, endDate));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Balance générale</h1>
        <p className="text-sm text-gray-500">Totaux des mouvements et soldes par compte.</p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Du</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Au</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Compte</th>
              <th className="text-left px-4 py-2 font-medium">Libellé</th>
              <th className="text-right px-4 py-2 font-medium">Débit</th>
              <th className="text-right px-4 py-2 font-medium">Crédit</th>
              <th className="text-right px-4 py-2 font-medium">Solde débiteur</th>
              <th className="text-right px-4 py-2 font-medium">Solde créditeur</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Chargement…</td></tr>
            ) : !data || data.lignes.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                Aucune écriture sur la période. Générez les écritures depuis l'accueil Comptabilité.
              </td></tr>
            ) : data.lignes.map((l) => (
              <tr key={l.compteNumero} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 font-mono">{l.compteNumero}</td>
                <td className="px-4 py-2">{l.libelle}</td>
                <td className="px-4 py-2 text-right">{formatTND(l.totalDebit)}</td>
                <td className="px-4 py-2 text-right">{formatTND(l.totalCredit)}</td>
                <td className="px-4 py-2 text-right">{l.soldeDebiteur ? formatTND(l.soldeDebiteur) : '—'}</td>
                <td className="px-4 py-2 text-right">{l.soldeCrediteur ? formatTND(l.soldeCrediteur) : '—'}</td>
              </tr>
            ))}
          </tbody>
          {data && data.lignes.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-gray-200 font-semibold bg-gray-50">
                <td className="px-4 py-2" colSpan={2}>Totaux</td>
                <td className="px-4 py-2 text-right">{formatTND(data.totalDebit)}</td>
                <td className="px-4 py-2 text-right">{formatTND(data.totalCredit)}</td>
                <td className="px-4 py-2 text-right">{formatTND(data.totalSoldeDebiteur)}</td>
                <td className="px-4 py-2 text-right">{formatTND(data.totalSoldeCrediteur)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {data && data.totalDebit !== data.totalCredit && (
        <p className="text-sm text-red-600">
          ⚠ Balance déséquilibrée (débit ≠ crédit) — vérifiez les écritures.
        </p>
      )}
    </div>
  );
};

export default Balance;
